// src/components/WaitlistView/WaitlistModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { clientService } from '../../services/clientService';
import { addToWaitlist, updateWaitlistEntry } from '../../services/waitlistService';
import { v4 as uuidv4 } from 'uuid';

const WaitlistModal = ({ isOpen, onClose, onAdded, editingEntry = null }) => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('normal');
  const [preferredType, setPreferredType] = useState('');
  const [timePreferences, setTimePreferences] = useState({
    morning: false,
    afternoon: false,
    evening: false
  });
  const [preferredStaff, setPreferredStaff] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Load clients once on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clientsData = await clientService.getAllClients();
        setClients(clientsData);
      } catch (error) {
        console.error('Error loading clients for waitlist:', error);
        setClients([]);
      }
    };
    loadClients();
  }, []);

  // Reset fields whenever modal opens or populate with editing data
  useEffect(() => {
    if (isOpen) {
      if (editingEntry) {
        // Populate form with existing entry data
        const client = clients.find(c => c.id === editingEntry.clientId);
        setSelectedClient(client);
        setSearchTerm(client ? client.name : '');
        setNotes(editingEntry.notes || '');
        setPriority(editingEntry.priority || 'normal');
        setPreferredType(editingEntry.preferredType || '');
        setTimePreferences(editingEntry.timePreferences || { morning: false, afternoon: false, evening: false });
        setPreferredStaff(editingEntry.preferredStaff || '');
      } else {
        // Reset fields for new entry
        setSearchTerm('');
        setFilteredClients([]);
        setSelectedClient(null);
        setNotes('');
        setPriority('normal');
        setPreferredType('');
        setTimePreferences({ morning: false, afternoon: false, evening: false });
        setPreferredStaff('');
      }
      // focus input
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, editingEntry, clients]);

  // Filter suggestions as user types
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const selectedName = selectedClient ? selectedClient.name.toLowerCase().trim() : '';
    if (term && term !== selectedName) {
      setFilteredClients(
        clients.filter(c => c.name.toLowerCase().includes(term))
      );
    } else {
      setFilteredClients([]);
    }
  }, [searchTerm, clients, selectedClient]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFilteredClients([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting a suggestion
  const handleSelect = client => {
    setSelectedClient(client);
    setSearchTerm(client.name);
    setFilteredClients([]);
    // blur input to close suggestions
    inputRef.current?.blur();
  };

  // Save waitlist entry (add new or update existing)
  const handleSave = async () => {
    if (!selectedClient) return;
    
    try {
      const entryData = {
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        clientEmail: selectedClient.email,
        clientPhone: selectedClient.phone,
        notes,
        priority,
        preferredType,
        timePreferences,
        preferredStaff,
        preferences: {
          timeOfDay: Object.keys(timePreferences).filter(key => timePreferences[key]),
          dayOfWeek: []
        }
      };
      
      let savedEntry;
      if (editingEntry) {
        // Update existing entry
        savedEntry = await updateWaitlistEntry(editingEntry.id, {
          ...entryData,
          updatedAt: new Date().toISOString()
        });
        savedEntry = { ...editingEntry, ...entryData, updatedAt: new Date().toISOString() };
      } else {
        // Add new entry
        savedEntry = await addToWaitlist(entryData);
      }
      
      onAdded(savedEntry);
      onClose();
    } catch (error) {
      console.error('Error saving waitlist entry:', error);
      // You might want to show an error toast here
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={containerRef} className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">
              {editingEntry ? 'Edit Waitlist Entry' : 'Add to Waitlist'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-200 rounded-full"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-8 space-y-8">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
            <input
              ref={inputRef}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Start typing to search clients..."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setSelectedClient(null);
              }}
            />
            {filteredClients.length > 0 && (
              <ul className="absolute z-20 bg-white border border-gray-300 rounded-lg mt-1 w-full max-h-48 overflow-auto shadow-lg">
                {filteredClients.map(c => (
                  <li
                    key={c.id}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                    onClick={() => handleSelect(c)}
                  >
                    {c.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={priority}
              onChange={e => setPriority(e.target.value)}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Appointment Type</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="e.g., Physiotherapy, Consultation..."
              value={preferredType}
              onChange={e => setPreferredType(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Preferences</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={timePreferences.morning}
                  onChange={e => setTimePreferences(prev => ({ ...prev, morning: e.target.checked }))}
                />
                <span className="ml-2 text-sm text-gray-700">Morning</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={timePreferences.afternoon}
                  onChange={e => setTimePreferences(prev => ({ ...prev, afternoon: e.target.checked }))}
                />
                <span className="ml-2 text-sm text-gray-700">Afternoon</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={timePreferences.evening}
                  onChange={e => setTimePreferences(prev => ({ ...prev, evening: e.target.checked }))}
                />
                <span className="ml-2 text-sm text-gray-700">Evening</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Staff Member</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="e.g., Dr. Smith, Any therapist..."
              value={preferredStaff}
              onChange={e => setPreferredStaff(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              rows={3}
              placeholder="Add any notes..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-200 flex justify-end gap-4">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
            onClick={handleSave}
          >
{editingEntry ? 'Save Changes' : 'Add to Waitlist'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaitlistModal;
